import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAccountBudgetTypeMappings, getExpenseTypeMappings, getUsefulLifeDetails } from 'src/api/app-sync-services';
import { logger } from 'src/analytics/KatalLogger';
import { Status } from 'src/models/AuthContextModels';
import {
  AccountBudgetTypeMapping,
  CompanyCurrencyMapping,
  ExpenseTypeMappingEntity,
  RevGpMapping,
  UsefulLifeMappingEntity
} from 'src/models/xPTMappingModels';
import { getFileFromS3URI } from 'src/utils/aws-s3-services';
import { getOnboardSegmentBucketName } from 'src/utils/xpt-s3-bucket-details';

interface xPTMapperState {
  expenseTypeStatus: Status;
  expenseTypeMappings: ExpenseTypeMappingEntity[];
  accountBudgetTypeMappingStatus: Status;
  accountBudgetTypeMapping: AccountBudgetTypeMapping[];
  usefulLifeStatus: Status;
  usefulLifeMappings: UsefulLifeMappingEntity[];
  companyCurrencyMappingStatus: Status;
  companyCurrencyMappings: CompanyCurrencyMapping[];
  revGpMappingStatus: Status;
  revGpMapping: RevGpMapping[];
}

const initialState: xPTMapperState = {
  expenseTypeStatus: Status.NotInitiated,
  expenseTypeMappings: [],
  accountBudgetTypeMappingStatus: Status.NotInitiated,
  accountBudgetTypeMapping: [],
  usefulLifeStatus: Status.NotInitiated,
  usefulLifeMappings: [],
  companyCurrencyMappingStatus: Status.NotInitiated,
  companyCurrencyMappings: [],
  revGpMappingStatus: Status.NotInitiated,
  revGpMapping: []
};

export const fetchExpenseTypeMappings = createAsyncThunk('xPTMapper/fetchExpenseTypeMappings', async () => await getExpenseTypeMappings());

export const fetchAccountBudgetTypeMappings = createAsyncThunk(
  'xPTMapper/fetchAccountBudgetTypeMappings',
  async () => await getAccountBudgetTypeMappings()
);

export const fetchUsefulLifeMappings = createAsyncThunk('xPTMapper/fetchUsefulLifeMappings', async () => await getUsefulLifeDetails());

/**
 * Type guard to validate if data matches CompanyCurrencyMapping interface
 */
const isCompanyCurrencyMapping = (item: any): item is CompanyCurrencyMapping => {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.company_code === 'string' &&
    typeof item.currency === 'string' &&
    item.company_code.trim() !== '' &&
    item.currency.trim() !== ''
  );
};

/**
 * Validates and transforms raw data to CompanyCurrencyMapping array
 */
const validateCompanyCurrencyMappings = (data: unknown): CompanyCurrencyMapping[] => {
  if (!Array.isArray(data)) {
    logger.error('Company currency mappings data is not an array', { receivedType: typeof data });
    throw new Error('Invalid data format: expected array of company currency mappings');
  }

  const validMappings: CompanyCurrencyMapping[] = [];
  const invalidItems: any[] = [];

  data.forEach((item, index) => {
    if (isCompanyCurrencyMapping(item)) {
      validMappings.push(item);
    } else {
      invalidItems.push({ index, item });
    }
  });

  if (invalidItems.length > 0) {
    logger.warn('Some company currency mapping items are invalid and will be filtered out', {
      totalItems: data.length,
      validItems: validMappings.length,
      invalidItems: invalidItems.length,
      invalidItemsDetails: invalidItems.slice(0, 3) // Log first 3 invalid items for debugging
    });
  }

  if (validMappings.length === 0) {
    logger.error('No valid company currency mappings found after validation');
    throw new Error('No valid company currency mappings found in the data');
  }

  return validMappings;
};

export const fetchCompanyCurrencyMappings = createAsyncThunk('xPTMapper/fetchCompanyCurrencyMappings', async () => {
  const bucketName = getOnboardSegmentBucketName();
  const s3URI = `s3://${bucketName}/mappings/list_company_currency.json`;

  try {
    const response = await getFileFromS3URI(s3URI);
    return validateCompanyCurrencyMappings(response);
  } catch (error: any) {
    logger.error('Failed to fetch or validate company currency mappings', {
      s3URI,
      error: error.message,
      stack: error.stack
    });
    throw new Error(`Failed to load company currency mappings: ${error.message}`);
  }
});

/**
 * Type guard to validate if data matches RevGpMapping interface
 */
const isRevGpMapping = (item: any): item is RevGpMapping => {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.channel === 'string' &&
    typeof item.product_line === 'string' &&
    typeof item.rev_gp === 'string' &&
    item.channel.trim() !== '' &&
    item.product_line.trim() !== '' &&
    item.rev_gp.trim() !== ''
  );
};

/**
 * Validates and transforms raw data to RevGpMapping array
 */
const validateRevGpMappings = (data: unknown): RevGpMapping[] => {
  if (!Array.isArray(data)) {
    logger.error('rev_gp mappings data is not an array', { receivedType: typeof data });
    throw new Error('Invalid data format: expected array of rev_gp mappings');
  }

  const validMappings: RevGpMapping[] = [];
  let invalidCount = 0;

  data.forEach((item) => {
    if (isRevGpMapping(item)) {
      // Normalize whitespace at the ingest boundary so every downstream consumer (row-level validator,
      // Onboarding cross-field check) can rely on clean keys without repeating .trim() on lookups.
      validMappings.push({
        channel: item.channel.trim(),
        product_line: item.product_line.trim(),
        rev_gp: item.rev_gp.trim()
      });
    } else {
      invalidCount++;
    }
  });

  if (invalidCount > 0) {
    logger.warn('Some rev_gp mapping items are invalid and will be filtered out', {
      totalItems: data.length,
      validItems: validMappings.length,
      invalidItems: invalidCount
    });
  }

  if (validMappings.length === 0) {
    logger.error('No valid rev_gp mappings found after validation');
    throw new Error('No valid rev_gp mappings found in the data');
  }

  return validMappings;
};

export const fetchRevGpMappings = createAsyncThunk('xPTMapper/fetchRevGpMappings', async () => {
  const bucketName = getOnboardSegmentBucketName();
  const s3URI = `s3://${bucketName}/mappings/list_rev_gp_mapping.json`;

  try {
    const response = await getFileFromS3URI(s3URI);
    return validateRevGpMappings(response);
  } catch (error: any) {
    logger.error('Failed to fetch or validate rev_gp mappings', {
      s3URI,
      error: error.message,
      stack: error.stack
    });
    throw new Error(`Failed to load rev_gp mappings: ${error.message}`);
  }
});

const xPTMapperSlice = createSlice({
  name: 'xPTMapper',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Expense Type Mappings
      .addCase(fetchExpenseTypeMappings.pending, (state) => {
        state.expenseTypeStatus = Status.Loading;
      })
      .addCase(fetchExpenseTypeMappings.fulfilled, (state, { payload }) => {
        state.expenseTypeMappings = payload;
        state.expenseTypeStatus = Status.Completed;
      })
      .addCase(fetchExpenseTypeMappings.rejected, (state) => {
        state.expenseTypeStatus = Status.Failed;
      })

      // Account Budget Type Mappings
      .addCase(fetchAccountBudgetTypeMappings.pending, (state) => {
        state.accountBudgetTypeMappingStatus = Status.Loading;
      })
      .addCase(fetchAccountBudgetTypeMappings.fulfilled, (state, { payload }) => {
        state.accountBudgetTypeMapping = payload;
        state.accountBudgetTypeMappingStatus = Status.Completed;
      })
      .addCase(fetchAccountBudgetTypeMappings.rejected, (state) => {
        state.accountBudgetTypeMappingStatus = Status.Failed;
      })

      // Useful Life Details
      .addCase(fetchUsefulLifeMappings.pending, (state) => {
        state.usefulLifeStatus = Status.Loading;
      })
      .addCase(fetchUsefulLifeMappings.fulfilled, (state, { payload }) => {
        state.usefulLifeMappings = payload;
        state.usefulLifeStatus = Status.Completed;
      })
      .addCase(fetchUsefulLifeMappings.rejected, (state) => {
        state.usefulLifeStatus = Status.Failed;
      })

      // Company Currency Mappings
      .addCase(fetchCompanyCurrencyMappings.pending, (state) => {
        state.companyCurrencyMappingStatus = Status.Loading;
      })
      .addCase(fetchCompanyCurrencyMappings.fulfilled, (state, { payload }) => {
        state.companyCurrencyMappings = payload;
        state.companyCurrencyMappingStatus = Status.Completed;
      })
      .addCase(fetchCompanyCurrencyMappings.rejected, (state) => {
        state.companyCurrencyMappingStatus = Status.Failed;
      })

      // rev_gp (Channel × Product Line) Mappings
      .addCase(fetchRevGpMappings.pending, (state) => {
        state.revGpMappingStatus = Status.Loading;
      })
      .addCase(fetchRevGpMappings.fulfilled, (state, { payload }) => {
        state.revGpMapping = payload;
        state.revGpMappingStatus = Status.Completed;
      })
      .addCase(fetchRevGpMappings.rejected, (state) => {
        state.revGpMappingStatus = Status.Failed;
      });
  }
});

export default xPTMapperSlice.reducer;
